import { captureException } from "@sentry/browser";
import { Immutable } from "immer";
import cookies from "js-cookie";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { load as loadUser } from "../api/user";
import {
    AddToCartType,
    RemoveFromCartType,
    RetailStoreEventCTAType,
    RetailStoreEventType,
} from "../constants";
import { IBasket, IBasketLine, IProduct } from "../models/catalogue";
import { RetailStore } from "../models/location";
import { IProductID, IProductSKU } from "../models/nominals";
import { IUser } from "../models/user";
import { getHighestDiscountName } from "./money";
import { getPageSetting } from "./settings";

// Initialize dataLayer if running in browser
const _window = getWindow();
if (_window && !_window.dataLayer) {
    _window.dataLayer = [];
}
// Utility function for more safety
const getWindowEnsureDataLayer = (): ReturnType<typeof getWindow> => {
    const window = getWindow();
    if (!window) {
        return null;
    }
    if (window.dataLayer === undefined) {
        window.dataLayer = [];
    }
    return window;
};

// Declare user hash name in the dataLayer
const USER_HASH = "USER_HASH";
const CURRENCY = "USD";

const getBasketCoupon = (basket: IBasket | null): string => {
    if (!basket) {
        return "";
    }
    const parts = [
        ...basket.voucher_discounts.map((d) => d.voucher.code),
        ...basket.offer_discounts.map((d) => d.name),
    ];
    return parts.length > 0 ? parts.join(", ") : "";
};

export interface IGA4ListMetadata {
    item_list_id?: string;
    item_list_name?: string;
    currency?: string;
}

/**
 * GA4 item payload schema. Mirrors the server-side ``build_catalog_item``
 * output in ``tsicommon_tracker_tags.py`` AFTER ``_compact_dict`` strips
 * ``None`` values, so every field is optional. Both client-built items
 * (``mapProductToAnalyticsItem``) and server-serialized items (passed via
 * Reactivated to ``trackViewItemListEventWithItems`` / ``trackSelectItemEventWithItem``)
 * conform to this shape.
 */
export interface IGA4ListItemPayload {
    item_id?: string;
    item_name?: string;
    item_brand?: string | null;
    item_variant?: string;
    item_size?: string | readonly string[] | null;
    item_category?: string;
    item_category2?: string | null;
    coupon?: string;
    quantity?: number;
    price?: number;
    discount?: number;
}

type IInternalGA4ItemPayload = IGA4ListItemPayload & {
    item_feel?: string | readonly string[] | null;
};

const getProductAnalyticsPrice = (product: IProduct) => {
    return product.price?.cosmetic_excl_tax
        ? parseFloat(product.price.cosmetic_excl_tax)
        : 0.0;
};

const mapProductToAnalyticsItem = (
    product: IProduct,
    {
        coupon,
        discount,
        includeItemFeel = false,
        quantity,
    }: {
        coupon?: string;
        discount?: number;
        includeItemFeel?: boolean;
        quantity?: number;
    } = {},
): IGA4ListItemPayload | IInternalGA4ItemPayload => {
    const item: IGA4ListItemPayload = {
        item_id: `${product.upc}`,
        item_name: product.title,
        item_brand: getPageSetting("app_display_name"),
        item_variant:
            product.skus.length > 0 ? `${product.skus[0]}` : undefined,
        item_size: product.attributes.option_size?.value,
        item_category: product.product_class_slug,
        item_category2: product.subtitle,
        coupon: coupon ?? "",
        quantity: quantity,
        price: getProductAnalyticsPrice(product),
        discount: discount,
    };

    if (!includeItemFeel) {
        return item;
    }

    return {
        ...item,
        item_feel: product.attributes.option_feel?.value,
    };
};

const mapProductToListEventItem = (product: IProduct): IGA4ListItemPayload => {
    return mapProductToAnalyticsItem(product);
};

const pushGA4EcommerceEvent = (
    event: string,
    items: ReadonlyArray<object>,
    currency: string | undefined,
    extraPayload: Record<string, unknown> = {},
) => {
    const window = getWindowEnsureDataLayer();
    if (!window) {
        return;
    }

    window.dataLayer.push({ items: undefined });
    window.dataLayer.push({
        event,
        ...(currency !== undefined ? { currency } : {}),
        items,
        ...extraPayload,
    });
};

const getBreadCrumb = () => {
    const window = getWindowEnsureDataLayer();
    const document = getDocument();
    if (!window || !document) {
        return "";
    }
    const location = "Home" + window.location.pathname;
    let breadcrumb = location.split("/").filter(Boolean).join(" > ");

    const onScreenBreadcrumb = document.querySelector<HTMLElement>(
        "[class^='breadcrumb']",
    );
    if (onScreenBreadcrumb) {
        breadcrumb = onScreenBreadcrumb.innerText.replace(/\n/g, "> ");
        if (!onScreenBreadcrumb.innerText.includes("home")) {
            return "Home > " + breadcrumb.trim();
        }
    }
    return breadcrumb.trim();
};
const getPageTitle = () => {
    const document = getDocument();
    if (!document) {
        return "";
    }
    const pageTitle: string | null = document.title.split(/\s([-–])|(\|)/)[0];
    return pageTitle.trim();
};

/**
 * Named counter with value stored in a cookie
 */
export class PersistentCounter {
    private readonly name: string;
    private readonly expiryMinutes: number;

    public constructor(name: string, expiryMinutes = 20) {
        this.name = name;
        this.expiryMinutes = expiryMinutes;
    }

    public get value(): number {
        const val = cookies.get(this.name) || "0";
        return parseInt(val, 10) || 0;
    }

    public set value(newValue: number) {
        const date = new Date();
        const expiryTimestamp = date.getTime() + this.expiryMinutes * 60 * 1000;
        date.setTime(expiryTimestamp);
        // Update cookie value
        cookies.set(this.name, `${newValue}`, {
            expires: date,
        });
        // Update datalayer value
        const window = getWindowEnsureDataLayer();
        if (!window) {
            return;
        }

        window.dataLayer.push({
            [`counter_${this.name}`]: newValue,
        });
    }

    public increment(amount = 1) {
        this.value += amount;
    }
}

/**
 * Track a page view
 */
export const track = (virtualURL: string, title?: string) => {
    const window = getWindowEnsureDataLayer();
    const document = getDocument();
    if (!window || !document) {
        return;
    }

    title = title === undefined ? document.title : title;
    window.dataLayer.push({
        event: "virtualPageview",
        virtualPageURL: virtualURL,
        virtualPageTitle: title,
    });
};

/**
 * Record a page view for a virtual (JS-rendered) subpage of the current (real) page.
 */
export const trackVirtualSubPage = (urlSegment: string) => {
    const window = getWindowEnsureDataLayer();
    if (!window) {
        return;
    }

    let page = window.location.href.replace(window.location.origin, "");
    page = page.replace(window.location.hash, "");
    page += "/" + urlSegment + "/";
    page = page.replace(/\/+/g, "/");
    page = page + window.location.hash;
    track(page);
};

/**
 * Send an event to our analytics platform(s)
 */
export const trackEvent = (
    eventCategory: string,
    eventAction: string,
    eventLabel?: string,
    eventValue?:
        | number
        | {
              nonInteraction?: boolean;
              [key: string]: unknown;
          },
) => {
    // Send event to Google Analytics
    const window = getWindowEnsureDataLayer();
    if (window && window.ga !== undefined) {
        window.ga(
            "send",
            "event",
            eventCategory,
            eventAction,
            eventLabel,
            eventValue,
        );
    }
};

export const hashPii = async (email: string): Promise<string> => {
    const normalized = email.toLowerCase().trim();
    if (!normalized) {
        return "";
    }
    const { sha256 } = await import("js-sha256");
    const atIndex = normalized.indexOf("@");
    if (atIndex === -1) {
        return sha256(normalized);
    }
    const username = normalized.slice(0, atIndex);
    const domain = normalized.slice(atIndex + 1);
    if (domain === "gmail.com" || domain === "googlemail.com") {
        const cleanUsername = username.split("+")[0].replace(/\./g, "");
        return sha256(`${cleanUsername}@${domain}`);
    }
    return sha256(normalized);
};

/**
 * The following functions are used in combination to encode the User's email into a SHA-256
 * hash and send the hash to the GTM dataLayer for marketing retargeting purposes.
 */
export const sendGTMUserHash = (hash?: string) => {
    // Emit a GTM event with the provided hash or try to pull the existing hash from localStorage
    const window = getWindowEnsureDataLayer();
    if (!window) {
        return;
    }

    let local_hash = "";
    try {
        const ls_hash = window.localStorage.getItem(USER_HASH);
        local_hash = ls_hash ? ls_hash : "";
    } catch (e) {
        local_hash = "";
    }
    const hash_to_send = hash ? hash : local_hash;
    if (hash_to_send) {
        window.dataLayer.push({
            USER_HASH: hash_to_send,
        });
    }
};

export const updateGTMUserHash = async (email: string) => {
    const { sha256 } = await import("js-sha256");
    // Generate the user hash from the email string and update
    // the dataLayer. (Called in user login forms)
    const cleanedEmail = email.toLowerCase().trim();
    // Convert cleaned_email into a Uint8Array and then generate a SHA-256 hash
    const userHash = sha256(cleanedEmail);
    const window = getWindowEnsureDataLayer();
    if (window) {
        try {
            window.localStorage.setItem(USER_HASH, userHash);
        } catch (e) {
            console.error(e);
        }
    }
    // Update window.dataLayer
    sendGTMUserHash(userHash);
};

export const kuidListeners = () => {
    // Add a form submit listener to email forms to trigger user hash
    // creation. KUID forms are indicated by adding the 'js-kuid-form' class
    const document = getDocument();
    if (!document) {
        return;
    }
    Array.prototype.forEach.call(
        document.querySelectorAll(".js-kuid-form"),
        (form: HTMLFormElement) => {
            form.addEventListener("submit", (event) => {
                const new_form = event.currentTarget as HTMLElement;
                if (new_form) {
                    const email_input =
                        new_form.querySelector<HTMLInputElement>(
                            'input[name="email"]',
                        );
                    if (email_input) {
                        updateGTMUserHash(email_input.value);
                    }
                }
            });
        },
    );
};

/**
 * GTM remarketing page view event analytics
 */
export const trackHomePageView = () => {
    const window = getWindowEnsureDataLayer();
    if (!window) {
        return;
    }

    window.dataLayer.push({
        event: "HomePageView",
        ecomm_pagetype: "home",
    });
};

export const trackSearchPageView = (prodIds: IProductSKU[]) => {
    const window = getWindowEnsureDataLayer();
    if (!window) {
        return;
    }

    window.dataLayer.push({
        event: "SearchPageView",
        ecomm_prodid: prodIds.slice(0, 3),
        ecomm_pagetype: "search",
    });
};

export const trackCategoryPageView = (prodIds: IProductSKU[]) => {
    const window = getWindowEnsureDataLayer();
    if (!window) {
        return;
    }

    window.dataLayer.push({
        event: "CategoryPageView",
        ecomm_prodid: prodIds.slice(0, 3),
        ecomm_pagetype: "category",
    });
};

export const trackPageView = async () => {
    const window = getWindowEnsureDataLayer();
    const document = getDocument();
    if (!window || !document) {
        return;
    }

    let pageType = document.body.dataset.trackPageType;
    if (!pageType) {
        pageType = document.body.dataset.trackEvent
            ? document.body.dataset.trackEvent?.replace("View", "")
            : "Landing Page";
    }
    const user: IUser = await loadUser();
    window.dataLayer.push({
        event: "page_view",
        userLoginStatus: user.has_password,
        pageType: pageType,
        userId: user.username,
        userType: user.has_password ? "Registered" : "Guest",
        userIsCSR: user.is_csr,
        breadcrumb: getBreadCrumb(),
        pageTitle: getPageTitle(),
    });
};

// for PDP, cart, checkout, conversion pages
export const trackProductValuePageView = (
    prodIds: ReadonlyArray<IProductSKU>,
    totalValue: string,
    pageType: string,
    event: string,
) => {
    const window = getWindowEnsureDataLayer();
    if (!window) {
        return;
    }

    window.dataLayer.push({
        event: event,
        ecomm_prodid: prodIds.slice(0, 3),
        ecomm_pagetype: pageType,
        ecomm_totalvalue: Number(totalValue),
    });
};

/**
 * Financing Events
 */
export const trackVariantSelectedEvent = (variantID: IProductID) => {
    const window = getWindowEnsureDataLayer();
    if (!window) {
        return;
    }
    // Push content to Google tag dataLayer
    window.dataLayer.push({
        event: "VARIANT_SELECTED",
        variant_id: variantID,
    });
};

export const trackConfiguratorLoadedEvent = () => {
    const window = getWindowEnsureDataLayer();
    if (!window) {
        return;
    }

    // update dataLayer to confirm the Configurator has finished loading
    window.dataLayer.push({
        configuratorLoaded: true,
    });
};

export const trackBasketEvent = (event: string, basket: IBasket) => {
    const window = getWindowEnsureDataLayer();
    if (!window) {
        return;
    }

    const includesCoupon = event === "begin_checkout" || event === "purchase";
    window.dataLayer.push({ items: undefined });
    window.dataLayer.push({
        event: event,
        currency: basket?.currency || CURRENCY,
        value: parseFloat(basket.total_excl_tax),
        ...(includesCoupon && { coupon: getBasketCoupon(basket) }),
        items: buildBasketLineItems(basket),
    });
};

/**
 * A FULLY-BUILT GA4 ecommerce event serialized server-side. Every prebuilt
 * event (view_item_list / select_item / view_promotion / select_promotion)
 * carries the required ``event`` name plus a heterogeneous, open-ended payload
 * (the reactivated-generated event types all expose an ``[k: string]:
 * unknown`` index signature alongside ``event``), so this is the most precise
 * structural type that covers every call site without re-narrowing per event.
 */
export type IPrebuiltGA4Event = Immutable<
    { event: string } & Record<string, unknown>
>;

/**
 * Push a FULLY-BUILT GA4 ecommerce event (serialized server-side by the same
 * ``tsicommon_tracker_tags`` builders the legacy Django tags used) to the
 * dataLayer VERBATIM, preceded by the standard ``{items: undefined}`` flush.
 * Used by SSR block ports (FeaturedOffersCardBlock / FeaturedProductsCardBlock)
 * whose entire event payload -- name/id resolution included -- is computed on
 * the server, so the client performs zero rebuild (a client rebuild risks a
 * payload mismatch, which is worse than dropping the event). No-ops on a null
 * event: the server serializes ``null`` for DNT requests and for cards with no
 * trackable target, matching the legacy tags' suppression behavior.
 */
export const pushPrebuiltGA4Event = (
    event: IPrebuiltGA4Event | null | undefined,
): void => {
    if (!event) {
        return;
    }
    const window = getWindowEnsureDataLayer();
    if (!window) {
        return;
    }
    window.dataLayer.push({ items: undefined });
    window.dataLayer.push({ ...event });
};

export const trackViewItemListEvent = (
    products: ReadonlyArray<IProduct>,
    listMetadata: IGA4ListMetadata = {},
) => {
    if (!products.length) {
        return;
    }

    pushGA4EcommerceEvent(
        "view_item_list",
        products.map((product) => mapProductToListEventItem(product)),
        listMetadata.currency || products[0].price?.currency || CURRENCY,
        {
            item_list_id: listMetadata.item_list_id,
            item_list_name: listMetadata.item_list_name,
        },
    );
};

/**
 * Push a ``view_item_list`` impression from PRE-BUILT GA4 item payloads that
 * were serialized server-side (via the ``build_catalog_item`` tracker helper),
 * rather than mapping hydrated ``IProduct``s client-side. Used by blocks that
 * SSR their product data so the on-load impression fires WITHOUT a client fetch
 * (the items match the legacy Django ``track_view_item_list`` tag output).
 */
export const trackViewItemListEventWithItems = (
    items: ReadonlyArray<IGA4ListItemPayload>,
    listMetadata: IGA4ListMetadata = {},
) => {
    if (!items.length) {
        return;
    }

    pushGA4EcommerceEvent(
        "view_item_list",
        items,
        listMetadata.currency || CURRENCY,
        {
            item_list_id: listMetadata.item_list_id,
            item_list_name: listMetadata.item_list_name,
        },
    );
};

export const trackSelectItemEvent = (
    product: IProduct | null,
    listMetadata: IGA4ListMetadata = {},
) => {
    if (!product) {
        return;
    }

    const selectedItem = mapProductToListEventItem(product);

    pushGA4EcommerceEvent(
        "select_item",
        [selectedItem],
        listMetadata.currency || product.price?.currency || CURRENCY,
        {
            item_list_id: listMetadata.item_list_id,
            item_list_name: listMetadata.item_list_name,
        },
    );
};

/**
 * Push a ``select_item`` click event from a PRE-BUILT GA4 item payload that was
 * serialized server-side (via the ``build_catalog_item`` tracker helper),
 * rather than mapping a hydrated ``IProduct`` client-side. This is the
 * ``select_item`` sibling of ``trackViewItemListEventWithItems``: used by SSR
 * blocks whose product link is rendered on the client but whose GA4 item is
 * serialized on the server, so the per-click event fires WITHOUT a client fetch
 * (reproducing the legacy ``select_item_tracking_attrs`` onclick). No-ops when
 * ``item`` is null -- the server emits null when there is no chosen product,
 * matching the legacy ``_build_select_item_event`` returning ``None``.
 */
export const trackSelectItemEventWithItem = (
    item: IGA4ListItemPayload | null,
    listMetadata: IGA4ListMetadata = {},
) => {
    if (!item) {
        return;
    }

    pushGA4EcommerceEvent(
        "select_item",
        [item],
        listMetadata.currency || CURRENCY,
        {
            item_list_id: listMetadata.item_list_id,
            item_list_name: listMetadata.item_list_name,
        },
    );
};

export const trackViewItemEvent = (product: IProduct) => {
    const window = getWindowEnsureDataLayer();
    if (!window || !product) {
        return;
    }
    const price = getProductAnalyticsPrice(product);
    window.dataLayer.push({ items: undefined });
    window.dataLayer.push({
        event: "view_item",
        currency: product.price?.currency || CURRENCY,
        value: price,
        items: [mapProductToAnalyticsItem(product, { includeItemFeel: true })],
    });
};

export const trackAddToBasketEvent = (
    product: IProduct | null,
    quantity?: number,
    addToCartType: AddToCartType = AddToCartType.ADDED,
    coupon: string | null = null,
) => {
    const window = getWindowEnsureDataLayer();
    if (!window || !product) {
        return;
    }

    const qty = quantity ?? 1;
    const price = getProductAnalyticsPrice(product);
    window.dataLayer.push({ items: undefined });
    window.dataLayer.push({
        event: "add_to_cart",
        currency: product.price?.currency || CURRENCY,
        value: price * qty,
        add_to_cart_type: addToCartType,
        items: [
            mapProductToAnalyticsItem(product, {
                coupon: coupon || "",
                quantity: qty,
                includeItemFeel: true,
            }),
        ],
    });
};

export const trackUpsellModalAddToCartEvent = (
    product: IProduct | null,
    quantity?: number,
) => {
    trackAddToBasketEvent(product, quantity, AddToCartType.UPSELL_MODAL_CTA);
};

export const trackRemoveFromBasketEvent = (
    product: IProduct | null,
    quantity?: number,
    removeFromCartType: RemoveFromCartType = RemoveFromCartType.DECREASE_QUANTITY,
    coupon: string | null = null,
) => {
    const window = getWindowEnsureDataLayer();
    if (!window || !product) {
        return;
    }

    const qty = quantity ?? 1;
    const price = getProductAnalyticsPrice(product);
    window.dataLayer.push({ items: undefined });
    window.dataLayer.push({
        event: "remove_from_cart",
        currency: product.price?.currency || CURRENCY,
        value: price * qty,
        remove_from_cart_type: removeFromCartType,
        items: [
            mapProductToAnalyticsItem(product, {
                coupon: coupon ?? "",
                quantity: qty,
                includeItemFeel: true,
            }),
        ],
    });
};

export const trackFormEvent = (
    event: "form_start" | "form_submit",
    name: `financing_${string}` | `checkout_${string}` | undefined,
    formElem: HTMLFormElement,
    basket: IBasket | null,
) => {
    const window = getWindowEnsureDataLayer();
    if (!window) {
        return;
    }

    window.dataLayer.push({ items: undefined });
    window.dataLayer.push({
        event: event,
        form_id: formElem.id || undefined,
        form_name: name || formElem.name,
        form_destination: formElem.action,
        currency: basket?.currency,
        value: basket ? parseFloat(basket.total_excl_tax) : 0.0,
        items: buildBasketLineItems(basket),
    });
};

const trackCheckoutStepEvent = (
    eventName: string,
    formName: string,
    formElem: HTMLFormElement,
    basket: IBasket | null,
    extraFields: Record<string, unknown> = {},
) => {
    const window = getWindowEnsureDataLayer();
    if (!window) {
        return;
    }
    window.dataLayer.push({ items: undefined });
    window.dataLayer.push({
        event: eventName,
        coupon: getBasketCoupon(basket),
        form_name: formName,
        form_destination: formElem.action,
        currency: basket?.currency,
        value: basket ? parseFloat(basket.total_excl_tax) : 0.0,
        items: buildBasketLineItems(basket),
        ...extraFields,
    });
};

export const trackStep1ShippingCompleteEvent = (
    formElem: HTMLFormElement,
    basket: IBasket | null,
) =>
    trackCheckoutStepEvent(
        "step_1_shipping_complete",
        "checkout_shipping",
        formElem,
        basket,
    );

export const trackStep2DeliveryCompleteEvent = (
    shippingTier: string,
    formElem: HTMLFormElement,
    basket: IBasket | null,
) =>
    trackCheckoutStepEvent(
        "step_2_delivery_complete",
        "checkout_delivery_options",
        formElem,
        basket,
        { shipping_tier: shippingTier },
    );

export const trackStep3BillingCompleteEvent = (
    formElem: HTMLFormElement,
    basket: IBasket | null,
) =>
    trackCheckoutStepEvent(
        "step_3_billing_complete",
        "checkout_billing",
        formElem,
        basket,
    );

export const trackStep4PaymentCompleteEvent = (
    paymentType: string,
    formElem: HTMLFormElement,
    basket: IBasket | null,
) =>
    trackCheckoutStepEvent(
        "step_4_payment_complete",
        "checkout_payment",
        formElem,
        basket,
        { payment_type: paymentType },
    );

const buildBasketLineItems = (basket: IBasket | null) =>
    basket?.lines.map((line: IBasketLine) => ({
        item_id: line.product.upc,
        item_name: line.product.title,
        item_brand: getPageSetting("app_display_name"),
        item_variant:
            line.product.skus.length > 0 ? line.product.skus[0] : undefined,
        item_size: line.product.attributes.option_size?.value,
        item_feel: line.product.attributes.option_feel?.value,
        item_category: line.product.product_class_slug,
        item_category2: line.product.subtitle,
        coupon: getHighestDiscountName(line.discounts) ?? "",
        quantity: line.quantity,
        price:
            line.quantity > 0
                ? parseFloat(line.line_price_excl_tax_incl_discounts) /
                  line.quantity
                : 0,
        discount:
            line.quantity > 0
                ? parseFloat(line.discount_value) / line.quantity
                : 0,
    })) ?? [];

const pushFinancingEvent = (
    eventName: string,
    formName: string,
    basket: IBasket | null,
) => {
    const window = getWindowEnsureDataLayer();
    if (!window) {
        return;
    }
    window.dataLayer.push({ items: undefined });
    window.dataLayer.push({
        event: eventName,
        form_name: formName,
        form_destination: window.location.href,
        cart_currency: basket?.currency || CURRENCY,
        cart_value: basket ? parseFloat(basket.total_excl_tax) : 0.0,
        cart_items: buildBasketLineItems(basket),
    });
};

export const trackPreQualOpenedEvent = (basket: IBasket | null) =>
    pushFinancingEvent("pre_qual_opened", "pre_qual_application", basket);

export const trackPreQualStartedEvent = (basket: IBasket | null) =>
    pushFinancingEvent("pre_qual_started", "pre_qual_application", basket);

export const trackPreQualSubmitSuccessEvent = (basket: IBasket | null) =>
    pushFinancingEvent(
        "pre_qual_submit_success",
        "pre_qual_application",
        basket,
    );

export const trackPreQualCompleteEvent = (basket: IBasket | null) =>
    pushFinancingEvent("pre_qual_complete", "pre_qual_application", basket);

export const trackCCAppOpenedEvent = (basket: IBasket | null) =>
    pushFinancingEvent("cc_app_opened", "cc_application", basket);

export const trackCCAppStartedEvent = (basket: IBasket | null) =>
    pushFinancingEvent("cc_app_started", "cc_application", basket);

export const trackCCStep1ContactInfoCompleteEvent = (basket: IBasket | null) =>
    pushFinancingEvent(
        "cc_step_1_contact_info_complete",
        "cc_application",
        basket,
    );

export const trackCCStep2FinancialInfoCompleteEvent = (
    basket: IBasket | null,
) =>
    pushFinancingEvent(
        "cc_step_2_financial_info_complete",
        "cc_application",
        basket,
    );

export const trackCCStep3IdentificationInfoCompleteEvent = (
    basket: IBasket | null,
) =>
    pushFinancingEvent(
        "cc_step_3_identification_info_complete",
        "cc_application",
        basket,
    );

export const trackCCStep4TermsDetailsCompleteEvent = (basket: IBasket | null) =>
    pushFinancingEvent(
        "cc_step_4_terms_details_complete",
        "cc_application",
        basket,
    );

export const trackCCAppCompleteEvent = (basket: IBasket | null) =>
    pushFinancingEvent("cc_app_complete", "cc_application", basket);

export const trackRetailStoreEvent = (
    event: RetailStoreEventType,
    store: Immutable<RetailStore> | null,
    ctaType: RetailStoreEventCTAType | null = null,
    linkText: string | null = null,
    linkUrl: string | null = null,
) => {
    const window = getWindowEnsureDataLayer();
    if (!window || !store) {
        return;
    }

    window.dataLayer.push({
        event: event,
        store_name: store.name,
        store_location: `${store.city}, ${store.state}`,
        cta_type: ctaType || undefined,
        link_text: linkText || undefined,
        link_url: linkUrl || undefined,
    });
};

export const trackSleepExpertEvent = (chatType: "call" | "chat") => {
    const window = getWindowEnsureDataLayer();
    if (!window) {
        return;
    }

    window.dataLayer.push({
        event: "sleep_expert_cta_event",
        start_chat: chatType,
    });
};

/**
 * Listrak Events
 */
export const trackListrackProductBrowse = (sku: IProductSKU) => {
    const window = getWindowEnsureDataLayer();
    if (
        !window ||
        window._ltk === undefined ||
        window._ltk_util === undefined
    ) {
        return;
    }

    window._ltk_util.ready(() => {
        window._ltk.Activity.AddProductBrowse(sku);
    });
};

/**
 * User Location Events
 */
export const trackUserLocation = (zip: string) => {
    const window = getWindowEnsureDataLayer();
    if (!window) {
        return;
    }

    window.dataLayer.push({
        event: "user_zipcode",
        zipcode: zip,
    });
};

const trackConfigEvent = (event: string, configName: string) => {
    const window = getWindowEnsureDataLayer();
    if (!window) {
        return;
    }
    window.dataLayer.push({
        event,
        config_name: configName,
    });
};

export const trackConfigUpsellShownEvent = (configName: string) =>
    trackConfigEvent("config_upsell_shown", configName);

export const trackConfigUpsellAddedEvent = (configName: string) =>
    trackConfigEvent("config_upsell_added", configName);

export const trackConfigUpsellRemovedEvent = (configName: string) =>
    trackConfigEvent("config_upsell_removed", configName);

export const trackConfigUpsellNoCheckboxShownEvent = (configName: string) =>
    trackConfigEvent("config_upsell_no_checkbox_shown", configName);

export const trackNewsletterSignup = async (email: string): Promise<void> => {
    const window = getWindowEnsureDataLayer();
    if (!window) {
        return;
    }
    const email_hash = await hashPii(email);
    window.dataLayer.push({
        event: "newsletter_signup",
        user_information: {
            user_email: email_hash,
        },
    });
};

export type OtpEventName =
    | "otp_prompt_shown"
    | "otp_code_sent"
    | "otp_code_resent"
    | "otp_code_entered"
    | "otp_verified"
    | "otp_verification_failed"
    | "otp_skipped";

export type OtpFormName =
    | "email_verification_gate"
    | "phone_verification_gate"
    | "verification_gate";

export type OtpChannel = "email" | "sms" | "voice";

export const trackOtpEvent = (
    eventName: OtpEventName,
    formName: OtpFormName,
    channel?: OtpChannel,
): void => {
    const window = getWindowEnsureDataLayer();
    if (!window) {
        return;
    }
    try {
        window.dataLayer.push({
            event: eventName,
            form_name: formName,
            form_destination: window.location.pathname,
            channel: channel ?? "unknown",
        });
    } catch (err) {
        console.error(err);
        captureException(err);
    }
};
